import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ConversationHeader.css';
import { LuEllipsisVertical, LuArrowLeft, LuChevronDown, LuSparkles, LuFilterX } from 'react-icons/lu';
import aviso, { textoDoErro } from '../lib/aviso';
import type { Contact } from '../data/mockData';
import { ChatService } from '../services/ChatService';
import MenuContexto from './MenuContexto';
import { itensDaConversa, type AcoesConversa } from './conversaMenu';

interface ConversationHeaderProps {
  contactName: string;
  contactPhone?: string;
  onToggleDetails: () => void;
  detailsOpen?: boolean;
  onResumir?: () => void;
  /** A conversa e as ações do kebab: a mesma lista do botão direito na lista (`conversaMenu.tsx`). */
  contato: Contact;
  acoes: AcoesConversa;
  conversationId: number;
  onBack?: () => void;
  tipoLeadId?: number;
  tipoLeadNome?: string;
  onLeadTypeChange?: () => void;
  /** Contato fora do funil: troca a pílula de temperatura pela etiqueta. */
  foraDoFunil?: boolean;
}

const LEADS = [
  { id: 1, label: 'Lead quente', classe: 'quente' },
  { id: 2, label: 'Lead morno', classe: 'morno' },
  { id: 3, label: 'Lead frio', classe: 'frio' },
];

const ConversationHeader: React.FC<ConversationHeaderProps> = ({
  contactName,
  contactPhone,
  onToggleDetails,
  detailsOpen,
  onResumir,
  contato,
  acoes,
  conversationId,
  onBack,
  tipoLeadId,
  tipoLeadNome,
  onLeadTypeChange,
  foraDoFunil,
}) => {
  const [menuAberto, setMenuAberto] = useState<{ x: number; y: number } | null>(null);
  const [isLeadMenuOpen, setLeadMenuOpen] = useState(false);
  const [isUpdatingLead, setIsUpdatingLead] = useState(false);
  const kebabRef = useRef<HTMLButtonElement>(null);
  const leadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLeadMenuOpen) return;
    const fora = (e: MouseEvent) => {
      if (!leadRef.current?.contains(e.target as Node)) setLeadMenuOpen(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [isLeadMenuOpen]);

  const abrirKebab = () => {
    const caixa = kebabRef.current?.getBoundingClientRect();
    if (caixa) setMenuAberto({ x: caixa.right, y: caixa.bottom + 4 });
  };
  const fecharKebab = useCallback(() => setMenuAberto(null), []);

  const lead = LEADS.find(l => l.id === tipoLeadId);
  const leadRotulo = tipoLeadNome || lead?.label || 'Sem classificação';
  const leadClasse = lead?.classe || 'sem';

  const handleLeadChange = async (novoId: number) => {
    setLeadMenuOpen(false);
    if (isUpdatingLead || novoId === tipoLeadId) return;

    setIsUpdatingLead(true);
    const toastId = aviso.carregando('Atualizando tipo de lead…');
    try {
      await ChatService.atualizarTipoLead(conversationId, novoId);
      aviso.ok('Tipo de lead atualizado.', { id: toastId });
      onLeadTypeChange?.();
    } catch (err: any) {
      aviso.erro(textoDoErro(err, 'Não foi possível atualizar o tipo de lead.'), { id: toastId });
    } finally {
      setIsUpdatingLead(false);
    }
  };

  return (
    <div className="ch-cvh">
      {onBack && (
        <button type="button" className="ch-bt ico" onClick={onBack} title="Voltar para a lista">
          <LuArrowLeft size={15} />
        </button>
      )}

      <span className="ch-av">{contactName.charAt(0).toUpperCase()}</span>

      <div className="ch-cvh-id">
        <div className="ch-cvh-nome">{contactName}</div>
        {contactPhone && <div className="ch-cvh-sub">{contactPhone}</div>}
      </div>

      {foraDoFunil ? (
        <span className="ch-fora-funil" title="Esta conversa não conta na Pipeline nem nas métricas">
          <LuFilterX size={11} />
          Fora do funil
        </span>
      ) : (
        <div className="ch-menu-wrap" ref={leadRef}>
          <button
            type="button"
            className={`ch-lead ${leadClasse}`}
            onClick={() => setLeadMenuOpen(a => !a)}
            style={{ opacity: isUpdatingLead ? 0.6 : 1 }}
          >
            {leadRotulo}
            <LuChevronDown size={11} />
          </button>
          {isLeadMenuOpen && (
            <div className="ch-menu" style={{ left: 0, right: 'auto', minWidth: 160 }}>
              {LEADS.map(opt => (
                <button key={opt.id} type="button" onClick={() => handleLeadChange(opt.id)}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="ch-cvh-acts">
        {onResumir && (
          <button type="button" className="ch-bt" onClick={onResumir}>
            <LuSparkles size={14} />
            Resumir
          </button>
        )}
        <button
          ref={kebabRef}
          type="button"
          className="ch-bt ico"
          /* Com o menu aberto, o `mousedown` do próprio botão não pode chegar ao "clique fora" do menu:
             ele o fecharia e o `click` logo depois o reabriria, em vez de fechar. */
          onMouseDown={e => { if (menuAberto) e.stopPropagation(); }}
          onClick={() => (menuAberto ? fecharKebab() : abrirKebab())}
          title="Mais ações"
          aria-haspopup="menu"
          aria-expanded={menuAberto !== null}
        >
          <LuEllipsisVertical size={15} />
        </button>
      </div>

      {menuAberto && (
        <MenuContexto
          itens={itensDaConversa(contato, acoes, { painel: { aberto: detailsOpen === true, alternar: onToggleDetails } })}
          posicao={menuAberto}
          alinharAoFim
          titulo={contactName}
          herdarDe={kebabRef.current}
          aoFechar={fecharKebab}
        />
      )}
    </div>
  );
};

export default ConversationHeader;
